"use client";

import {
  AiType,
  BackgroundImage,
  HeroAiTypeButtons,
  HeroDescription,
  HeroTitle,
} from "@/components/hero/HeroComponents";
import { useEffect, useState } from "react";

import HeroAssistants from "@/components/hero/HeroAssistants";
import clsx from "clsx";
import styles from "./Hero.module.css";
import { useTranslation } from "react-i18next";

const Hero = () => {
  const { t } = useTranslation();

  const [assistant, setAssistant] = useState<
    "insurance" | "banking" | "finance"
  >("insurance");

  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(
    null
  );

  // Get suggestions based on the selected assistant tab
  const getAiSuggestions = (
    assistantType: "insurance" | "banking" | "finance"
  ) => {
    return [0, 1, 2].map((index) => ({
      label: t(`hero.tabs.${assistantType}.suggestions.${index}`).toUpperCase(),
      key: `${assistantType}-suggestion-${index}`,
      index,
    }));
  };

  // Get the correct suggestions based on the current assistant
  const aiTypes: AiType[] = getAiSuggestions(assistant);

  const aiAssistants: AiType[] = [
    {
      label: t("hero.tabs.insurance.title"),
      key: "insurance",
    },
    {
      label: t("hero.tabs.banking.title"),
      key: "banking",
    },
    {
      label: t("hero.tabs.finance.title"),
      key: "finance",
    },
  ];

  const handleSuggestionClick = (key: string, index: number) => {
    // Only show contact form for the first suggestion (index 0)
    if (index === 0) {
      setSelectedSuggestion(key);
    } else {
      // For other suggestions, send the suggestion text to the iframe
      // We set the key as selectedSuggestion which will be used by HeroAssistants
      // to send the message to the iframe
      setSelectedSuggestion(key);
    }
  };

  // Reset the selected suggestion when assistant type changes
  useEffect(() => {
    setSelectedSuggestion(null);
  }, [assistant]);

  return (
    <>
      {/* Desktop */}
      <div
        className={clsx(
          "relative",
          "lg:h-[800px]",
          "overflow-hidden",
          "rounded-2xl",
          "max-w-8xl",
          "mx-auto",
          "items-center",
          "justify-center",
          "hidden",
          "lg:flex"
        )}
      >
        <BackgroundImage />
        <div
          className={clsx(
            "relative",
            "flex",
            "h-[80%]",
            "items-center",
            "justify-center",
            "w-full",
            "flex-col",
            "px-12"
          )}
        >
          <div
            className={clsx(
              "grid",
              "grid-cols-1",
              "lg:grid-cols-2",
              "max-w-6xl",
              "w-full",
              "h-full",
              styles.gradientBorder
            )}
          >
            <div
              className={clsx(
                "flex",
                "flex-col",
                "text-white",
                "space-y-6",
                "lg:min-h-[500px]",
                "mb-8",
                "md:mb-0",
                "relative",
                "justify-center"
              )}
            >
              <HeroTitle title={t("hero.title")} />
              <div className="border-t border-white w-2/3 my-6 mb-12 mx-auto"></div>
              <HeroDescription description={t("hero.description")} />
            </div>
            <HeroAssistants
              aiAssistants={aiAssistants}
              assistant={assistant}
              setAssistant={setAssistant}
              selectedSuggestion={selectedSuggestion}
              setSelectedSuggestion={setSelectedSuggestion}
            />
          </div>
          <div
            className={clsx(
              "w-full",
              "flex",
              "justify-end",
              "mt-4",
              "max-w-6xl"
            )}
          >
            <div className={clsx("w-full", "max-w-lg", "md:-mr-6 xl:mr-0")}>
              <HeroAiTypeButtons
                aiTypes={aiTypes}
                onSuggestionClick={handleSuggestionClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden">
        <HeroTitle title={t("hero.title")} />
        <HeroDescription description={t("hero.description")} />
        <div className="bg-[url('/images/hero_sm.webp')] lg:bg-[url('/images/hero.webp')] bg-cover bg-center h-[700px] w-full p-4 flex flex-col justify-center">
          <HeroAssistants
            aiAssistants={aiAssistants}
            assistant={assistant}
            setAssistant={setAssistant}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
          />
          <HeroAiTypeButtons
            aiTypes={aiTypes}
            isMobile
            onSuggestionClick={handleSuggestionClick}
          />
        </div>
      </div>
    </>
  );
};

export default Hero;
